import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { addComment, deleteComment } from '../services/ApiService';
import type { Comment } from '../types/models';
import { Send, Trash2, MessageCircle } from 'lucide-react';
import { AnalyticsEvents, track } from '../services/AnalyticsService';

interface Props {
  orgId: string;
  postId: string;
  initialComments: Comment[];
}

export default function PostComments({ orgId, postId, initialComments }: Props) {
  const { user } = useAuth();
  const { isBoardMember } = useOrganization();
  const [comments, setComments] = useState<Comment[]>(initialComments);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !newComment.trim()) return;
    setSubmitting(true);

    try {
      const comment = await addComment(orgId, postId, newComment.trim());
      setComments(prev => [...prev, comment]);
      track(AnalyticsEvents.CommentAdded, { orgId, postId });
      setNewComment('');
      setSubmitError('');
    } catch {
      setSubmitError('Kunde inte skicka kommentaren. Försök igen.');
    } finally {
      setSubmitting(false);
    }
  }, [user, orgId, postId, newComment]);

  const handleDelete = useCallback(async (commentId: string) => {
    try {
      await deleteComment(orgId, postId, commentId);
      setComments(prev => prev.filter(c => c.id !== commentId));
      setSubmitError('');
    } catch {
      setSubmitError('Kunde inte ta bort kommentaren. Försök igen.');
    }
  }, [orgId, postId]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHr = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Just nu';
    if (diffMin < 60) return `${diffMin} min sedan`;
    if (diffHr < 24) return `${diffHr} tim sedan`;
    if (diffDay < 7) return `${diffDay} dagar sedan`;
    return d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
  };

  const previewCount = 2;
  const hasMore = comments.length > previewCount;
  const visibleComments = expanded ? comments : comments.slice(-previewCount);

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-3"
      >
        <MessageCircle className="w-4 h-4" />
        {comments.length === 0
          ? 'Kommentarer'
          : `${comments.length} kommentar${comments.length !== 1 ? 'er' : ''}`}
      </button>

      {comments.length > 0 && (
        <div className="space-y-3 mb-3">
          {hasMore && !expanded && (
            <button
              onClick={() => setExpanded(true)}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium transition-colors"
            >
              Visa alla {comments.length} kommentarer
            </button>
          )}
          {visibleComments.map(c => (
            <div key={c.id} className="flex gap-2.5 group">
              <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-600 flex-shrink-0 mt-0.5">
                {c.author.fullName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="bg-gray-50 rounded-xl px-3.5 py-2.5">
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-gray-900">{c.author.fullName}</span>
                    <span className="text-xs text-gray-400">{formatDate(c.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700 mt-0.5 whitespace-pre-wrap">{c.content}</p>
                </div>
              </div>
              {(c.author.id === user?.id || isBoardMember) && (
                <button
                  onClick={() => handleDelete(c.id)}
                  className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all self-center"
                >
                  <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                </button>
              )}
            </div>
          ))}
          {hasMore && expanded && (
            <button
              onClick={() => setExpanded(false)}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              Visa farre
            </button>
          )}
        </div>
      )}

      {submitError && (
        <p className="text-xs text-red-600 mb-2">{submitError}</p>
      )}
      <form onSubmit={handleSubmit} className="flex gap-2">
        <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center text-xs font-semibold text-teal-700 flex-shrink-0 mt-0.5">
          {user?.fullName?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div className="flex-1 flex gap-2">
          <input
            type="text"
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Skriv en kommentar..."
            className="flex-1 px-3.5 py-2 text-sm rounded-xl border border-gray-200 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-gray-50 focus:bg-white transition-colors"
          />
          <button
            type="submit"
            disabled={submitting || !newComment.trim()}
            className="p-2 rounded-xl bg-teal-700 hover:bg-teal-800 text-white disabled:opacity-30 disabled:hover:bg-teal-700 transition-colors flex-shrink-0"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </form>
    </div>
  );
}
